import { useState, useMemo, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Upload,
  Calendar,
  Plus,
  FileText,
  Download,
  Eye,
  User,
  Building2,
  MapPin,
  Clock,
  Users,
  ChevronDown,
} from "lucide-react";
import { FILTER_TYPES, DEFAULT_FILTER } from "./InteractiveAnnualWheel";
import {
  fetchEvents,
  createEvent,
  deleteEvent,
} from "../../services/businessDashApi";

const INVITED_GROUPS = [
  { id: "clients",    label: "Clients" },
  { id: "families",   label: "Families" },
  { id: "board",      label: "Board" },
  { id: "employees",  label: "Employees" },
  { id: "sales",      label: "Sales Department" },
  { id: "marketing",  label: "Marketing Department" },
  { id: "hr",         label: "HR Department" },
  { id: "production", label: "Production Department" },
  { id: "management", label: "Management" },
];

const MONTHS = ["", "January","February","March","April","May","June",
                 "July","August","September","October","November","December"];

// ─── Add Event Form ───────────────────────────────────────────────────────────

const AddEventForm = ({ onSave, onCancel }) => {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [isGroupsOpen, setIsGroupsOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    location: "",
    description: "",
    responsible: "",
  });

  const toggleGroup = (groupId) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const getSelectedLabels = () => {
    if (selectedGroups.length === 0) return "Select groups...";
    if (selectedGroups.length <= 2)
      return selectedGroups
        .map((id) => INVITED_GROUPS.find((g) => g.id === id)?.label)
        .join(", ");
    return `${selectedGroups.length} groups selected`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.startDate) return;
    onSave?.({
      ...form,
      invitedGroups: selectedGroups,
    });
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <Label htmlFor="eventTitle">Event Title</Label>
        <Input
          id="eventTitle"
          placeholder="Enter event title"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="eventDate">Start Date</Label>
          <Input
            id="eventDate"
            type="date"
            value={form.startDate}
            onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
            required
          />
        </div>
        <div>
          <Label htmlFor="eventEndDate">
            End Date{" "}
            <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Input
            id="eventEndDate"
            type="date"
            value={form.endDate}
            onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="startTime">Start Time</Label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="startTime"
              type="time"
              className="pl-9"
              value={form.startTime}
              onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="endTime">End Time</Label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="endTime"
              type="time"
              className="pl-9"
              value={form.endTime}
              onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
            />
          </div>
        </div>
      </div>

      <div>
        <Label htmlFor="location">Location</Label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="location"
            placeholder="Enter location or address"
            className="pl-9"
            value={form.location}
            onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <Label>Invited Groups</Label>
        <Popover open={isGroupsOpen} onOpenChange={setIsGroupsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-between mt-1 font-normal">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className={selectedGroups.length === 0 ? "text-muted-foreground" : ""}>
                  {getSelectedLabels()}
                </span>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[300px] p-0 bg-background border border-border shadow-lg z-50"
            align="start"
          >
            <div className="p-2 space-y-1 max-h-[250px] overflow-y-auto">
              {INVITED_GROUPS.map((group) => (
                <div
                  key={group.id}
                  className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                  onClick={() => toggleGroup(group.id)}
                >
                  <Checkbox
                    id={`group-${group.id}`}
                    checked={selectedGroups.includes(group.id)}
                    onCheckedChange={() => toggleGroup(group.id)}
                  />
                  <Label
                    htmlFor={`group-${group.id}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {group.label}
                  </Label>
                </div>
              ))}
            </div>
            {selectedGroups.length > 0 && (
              <div className="border-t border-border p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => setSelectedGroups([])}
                >
                  Clear all
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {selectedGroups.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {selectedGroups.map((groupId) => {
              const group = INVITED_GROUPS.find((g) => g.id === groupId);
              return (
                <Badge
                  key={groupId}
                  variant="secondary"
                  className="text-xs cursor-pointer hover:bg-destructive/20"
                  onClick={() => toggleGroup(groupId)}
                >
                  {group?.label} ×
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="eventDescription">Description</Label>
        <Textarea
          id="eventDescription"
          placeholder="Brief description of the event"
          rows={2}
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
        />
      </div>

      <div>
        <Label htmlFor="eventResponsible">Responsible</Label>
        <Input
          id="eventResponsible"
          placeholder="Department or person"
          value={form.responsible}
          onChange={(e) => setForm((p) => ({ ...p, responsible: e.target.value }))}
        />
      </div>

      <div className="border-2 border-dashed border-muted rounded-lg p-3 text-center bg-muted/20">
        <Upload className="w-5 h-5 text-muted-foreground mx-auto mb-1" />
        <p className="text-xs text-muted-foreground mb-2">Upload documents</p>
        <Button type="button" variant="outline" size="sm">
          Choose Files
        </Button>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm">
          Add Event
        </Button>
      </div>
    </form>
  );
};

// ─── Mock events (fallback when API unavailable) ──────────────────────────────

const MOCK_EVENTS = [
  { id: 1,  date: "2025-01-15", name: "Annual Strategy Kickoff",  type: "Corporate Event", quarter: 1, month: 1,  category: "corporate",     description: "Annual strategy planning session with executive team",    responsible: "CEO Office",      documents: ["Strategy_Kickoff_2025.pdf"] },
  { id: 2,  date: "2025-02-20", name: "Q1 All-Hands Meeting",     type: "Corporate Event", quarter: 1, month: 2,  category: "corporate",     description: "Company-wide quarterly update meeting",                   responsible: "HR Department",   documents: [] },
  { id: 3,  date: "2025-03-15", name: "ESG Audit Review",         type: "Sustainability",  quarter: 1, month: 3,  category: "sustainability", description: "Environmental, Social and Governance audit review",        responsible: "Sarah Johnson",   documents: ["ESG_Audit_Report_2025.pdf"] },
  { id: 4,  date: "2025-04-10", name: "Annual General Meeting",   type: "Corporate Event", quarter: 2, month: 4,  category: "corporate",     description: "Annual shareholder meeting",                              responsible: "Board Secretary", documents: ["AGM_Agenda.pdf", "Annual_Report_2024.pdf"] },
  { id: 5,  date: "2025-05-15", name: "Q2 All-Hands Meeting",     type: "Corporate Event", quarter: 2, month: 5,  category: "corporate",     description: "Company-wide quarterly update meeting",                   responsible: "HR Department",   documents: [] },
  { id: 6,  date: "2025-06-20", name: "Sustainability Workshop",  type: "Sustainability",  quarter: 2, month: 6,  category: "sustainability", description: "Staff training on sustainability reporting requirements",  responsible: "ESG Committee",   documents: [] },
  { id: 7,  date: "2025-07-10", name: "Mid-Year Strategy Review", type: "Corporate Event", quarter: 3, month: 7,  category: "corporate",     description: "Half-year strategy performance review",                   responsible: "CEO Office",      documents: ["H1_Strategy_Review.pdf"] },
  { id: 8,  date: "2025-08-15", name: "Q3 All-Hands Meeting",     type: "Corporate Event", quarter: 3, month: 8,  category: "corporate",     description: "Company-wide quarterly update meeting",                   responsible: "HR Department",   documents: [] },
  { id: 9,  date: "2025-09-05", name: "Factory Audit",            type: "Sustainability",  quarter: 3, month: 9,  category: "sustainability", description: "Supply chain factory audit for ethical compliance",        responsible: "Operations Team", documents: ["Factory_Audit_Checklist.pdf"] },
  { id: 10, date: "2025-10-18", name: "Carbon Footprint Review",  type: "Sustainability",  quarter: 4, month: 10, category: "sustainability", description: "Annual carbon footprint assessment",                      responsible: "ESG Committee",   documents: ["Carbon_Report_2025.pdf"] },
  { id: 11, date: "2025-11-15", name: "Q4 All-Hands Meeting",     type: "Corporate Event", quarter: 4, month: 11, category: "corporate",     description: "Company-wide quarterly update meeting",                   responsible: "HR Department",   documents: [] },
  { id: 12, date: "2025-12-10", name: "Year-End Celebration",     type: "Corporate Event", quarter: 4, month: 12, category: "corporate",     description: "Annual company celebration event",                        responsible: "HR Department",   documents: [] },
];

// ─── Main panel ───────────────────────────────────────────────────────────────

const CorporateEventsPanel = ({ activeFilter = DEFAULT_FILTER }) => {
  const [allEvents, setAllEvents] = useState(MOCK_EVENTS);
  const [addOpen, setAddOpen] = useState(false);

  // Try to load from backend
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchEvents();
        const list = Array.isArray(data) ? data : data?.results ?? data?.data ?? [];
        if (list.length > 0) {
          setAllEvents(
            list.map((item) => {
              const d = new Date(item.date || item.start_date || item.event_date);
              return {
                id:          item.id,
                date:        item.date || item.start_date || item.event_date,
                name:        item.name || item.title,
                type:        item.type || item.event_type || "Corporate Event",
                quarter:     Math.ceil((d.getMonth() + 1) / 3),
                month:       d.getMonth() + 1,
                category:    item.category || "corporate",
                description: item.description || "",
                responsible: item.responsible || item.organiser || "",
                documents:   item.documents || [],
              };
            })
          );
        }
      } catch {
        // Fall back to mock data silently
      }
    };
    load();
  }, []);

  const filteredEvents = useMemo(() => {
    if (!activeFilter || activeFilter.type === FILTER_TYPES.YEAR) return allEvents;
    if (activeFilter.type === FILTER_TYPES.MONTH)
      return allEvents.filter((e) => e.month === activeFilter.value);
    if (activeFilter.type === FILTER_TYPES.QUARTER)
      return allEvents.filter((e) => e.quarter === activeFilter.value);
    return allEvents;
  }, [allEvents, activeFilter]);

  const getFilterLabel = () => {
    if (!activeFilter || activeFilter.type === FILTER_TYPES.YEAR) return "All Events";
    if (activeFilter.type === FILTER_TYPES.MONTH)
      return `${MONTHS[activeFilter.value]} Events`;
    if (activeFilter.type === FILTER_TYPES.QUARTER)
      return `Q${activeFilter.value} Events`;
    return "Events";
  };

  const getEventTypeColor = (type) => {
    if (type === "Corporate Event") return "bg-[hsl(var(--pastel-blue))] text-foreground";
    if (type === "Sustainability")  return "bg-[hsl(var(--pastel-green,var(--pastel-blue)))] text-foreground";
    return "bg-muted text-foreground";
  };

  const handleSaveEvent = async (formData) => {
    try {
      const payload = {
        title:       formData.title,
        description: formData.description,
        date:        formData.startDate,
        responsible: formData.responsible,
        location:    formData.location,
        event_type:  "company",
      };
      const created = await createEvent(payload);
      const d = new Date(formData.startDate);
      setAllEvents((prev) => [
        ...prev,
        {
          id:          created?.id ?? Date.now(),
          date:        formData.startDate,
          name:        formData.title,
          type:        "Corporate Event",
          quarter:     Math.ceil((d.getMonth() + 1) / 3),
          month:       d.getMonth() + 1,
          category:    "corporate",
          description: formData.description || "",
          responsible: formData.responsible || "",
          documents:   [],
        },
      ]);
    } catch {
      // Optimistic add with local ID
      const d = new Date(formData.startDate);
      setAllEvents((prev) => [
        ...prev,
        {
          id:          Date.now(),
          date:        formData.startDate,
          name:        formData.title,
          type:        "Corporate Event",
          quarter:     Math.ceil((d.getMonth() + 1) / 3),
          month:       d.getMonth() + 1,
          category:    "corporate",
          description: formData.description || "",
          responsible: formData.responsible || "",
          documents:   [],
        },
      ]);
    }
    setAddOpen(false);
  };

  return (
    <Card className="border-0 shadow-soft rounded-soft bg-card/50 backdrop-blur-sm h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-lg font-medium">
          <Building2 className="w-5 h-5 text-primary" />
          Corporate Events
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {getFilterLabel()} •{" "}
          {filteredEvents.length} event{filteredEvents.length !== 1 ? "s" : ""}
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Events list */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {filteredEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No events for this period
            </div>
          ) : (
            filteredEvents.map((event) => (
              <Dialog key={event.id}>
                <DialogTrigger asChild>
                  <div className="p-3 bg-background rounded-soft border border-muted/20 hover:shadow-sm hover:border-primary/20 transition-all cursor-pointer">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground text-sm truncate">
                          {event.name}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {event.description}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <Badge variant="outline" className="text-[10px] whitespace-nowrap">
                          {new Date(event.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                          })}
                        </Badge>
                        <Badge className={`text-[10px] ${getEventTypeColor(event.type)}`}>
                          {event.type === "Corporate Event" ? "Corporate" : "ESG"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md bg-background">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      {event.name}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Date</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {new Date(event.date).toLocaleDateString("en-GB", {
                          weekday: "long",
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Description</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        {event.description}
                      </p>
                    </div>
                    {event.responsible && (
                      <div>
                        <Label className="text-sm font-medium">Responsible</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            {event.responsible}
                          </p>
                        </div>
                      </div>
                    )}
                    {event.documents?.length > 0 && (
                      <div>
                        <Label className="text-sm font-medium">Documents</Label>
                        <div className="space-y-2 mt-2">
                          {event.documents.map((doc, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between p-2 bg-muted/20 rounded"
                            >
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm">{doc}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                                  <Download className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            ))
          )}
        </div>

        {/* Add Event button */}
        <div className="flex justify-center pt-2">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full w-10 h-10 p-0">
                <Plus className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg bg-background max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Corporate Event</DialogTitle>
              </DialogHeader>
              <AddEventForm
                onSave={handleSaveEvent}
                onCancel={() => setAddOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
};

export default CorporateEventsPanel;
